/**
 * Walls letting go, and the parts that were behind them.
 *
 * Two effects with one owner, because they are one event: a compartment gives
 * way, and what comes out of it is debris and a shower of hull.
 *
 * ── Sparks ─────────────────────────────────────────────────────────────────
 *
 * Drawn as points rather than as the wall itself. The wall is still in the
 * blueprint and still drawn — a broken line is a hole with an edge, not an
 * absence — so what is needed here is the *moment*, and a moment is better told
 * by something that leaves than by something that vanishes. They fly out along
 * the wall's own normal and fall away, which reads as the wall having failed
 * outward rather than as an explosion centred on nothing.
 *
 * ── Debris ─────────────────────────────────────────────────────────────────
 *
 * A part that survived its compartment is left where the ship was, drifting on
 * a fraction of the ship's own momentum, and can be flown back into. Collecting
 * needs a collector aboard, which is the one thing on the parts list that had no
 * job until now.
 *
 * Debris lives in the flight scene and dies with it. A crossing tears the whole
 * system down, so anything you left behind is genuinely left behind — which is a
 * consequence worth having rather than a limitation to apologise for.
 */

import * as THREE from 'three';

import type { ComponentType } from '@interspacial/shared';

/** How long a wall's failure is visible, in seconds. */
const SPARK_LIFE = 1.1;

/** How many points a single wall throws. */
const SPARK_COUNT = 26;

/** How fast they leave, in world units a second. */
const SPARK_SPEED = 14;

/** How close a hull has to be to sweep a part up, in world units. */
export const COLLECT_RANGE = 26;

export interface Debris {
  type: ComponentType;
  at: THREE.Vector3;
  drift: THREE.Vector3;
  mesh: THREE.Object3D;
}

export interface Wreckage {
  group: THREE.Group;
  /** A wall has failed at this point, throwing along this normal. */
  spark: (at: THREE.Vector3, along: THREE.Vector3) => void;
  /** A part is now loose here. */
  drop: (type: ComponentType, at: THREE.Vector3, drift: THREE.Vector3) => void;
  /**
   * Advance everything, and report what the hull swept up.
   *
   * `origin` is the scene's rebase — everything here is stored in absolute
   * coordinates like `shipAt`, because debris outlives several rebases and
   * storing it in scene space would have it slide across the system.
   */
  step: (seconds: number, shipAt: THREE.Vector3, origin: THREE.Vector3, collecting: boolean) => ComponentType[];
  dispose: () => void;
}

export function createWreckage(): Wreckage {
  const group = new THREE.Group();

  const sparkGeometry = new THREE.BufferGeometry();
  const positions = new Float32Array(SPARK_COUNT * 3);
  sparkGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const bursts: {
    at: THREE.Vector3;
    velocities: THREE.Vector3[];
    left: number;
    points: THREE.Points;
  }[] = [];

  const debris: Debris[] = [];

  const partGeometry = new THREE.OctahedronGeometry(1.6);

  const spark = (at: THREE.Vector3, along: THREE.Vector3) => {
    const geometry = sparkGeometry.clone();
    const material = new THREE.PointsMaterial({
      color: 0xffb347,
      size: 1.6,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < SPARK_COUNT; i++) {
      // Out along the wall's normal, spread into a cone rather than a plane —
      // a flat fan reads as a decal, and this has to read as material leaving.
      const scatter = new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5,
      ).multiplyScalar(0.8);

      velocities.push(
        along.clone().add(scatter).normalize().multiplyScalar(SPARK_SPEED * (0.35 + Math.random())),
      );
    }

    group.add(points);
    bursts.push({ at: at.clone(), velocities, left: SPARK_LIFE, points });
  };

  const drop = (type: ComponentType, at: THREE.Vector3, drift: THREE.Vector3) => {
    const mesh = new THREE.Mesh(
      partGeometry,
      new THREE.MeshBasicMaterial({ color: 0x9fd0ff, wireframe: true }),
    );
    group.add(mesh);
    debris.push({ type, at: at.clone(), drift: drift.clone(), mesh });
  };

  const step = (
    seconds: number,
    shipAt: THREE.Vector3,
    origin: THREE.Vector3,
    collecting: boolean,
  ): ComponentType[] => {
    // ── Sparks ─────────────────────────────────────────────────────────────
    for (let i = bursts.length - 1; i >= 0; i--) {
      const burst = bursts[i]!;
      burst.left -= seconds;

      if (burst.left <= 0) {
        group.remove(burst.points);
        burst.points.geometry.dispose();
        (burst.points.material as THREE.Material).dispose();
        bursts.splice(i, 1);
        continue;
      }

      const age = SPARK_LIFE - burst.left;
      const attribute = burst.points.geometry.getAttribute('position') as THREE.BufferAttribute;

      for (let p = 0; p < SPARK_COUNT; p++) {
        const velocity = burst.velocities[p]!;
        attribute.setXYZ(
          p,
          burst.at.x - origin.x + velocity.x * age,
          burst.at.y - origin.y + velocity.y * age,
          burst.at.z - origin.z + velocity.z * age,
        );
      }
      attribute.needsUpdate = true;
      burst.points.geometry.computeBoundingSphere();

      // Fading and slowing together, so the last of it goes out rather than
      // simply being switched off mid-flight.
      (burst.points.material as THREE.PointsMaterial).opacity = burst.left / SPARK_LIFE;
    }

    // ── Debris ─────────────────────────────────────────────────────────────
    const collected: ComponentType[] = [];

    for (let i = debris.length - 1; i >= 0; i--) {
      const part = debris[i]!;
      part.at.addScaledVector(part.drift, seconds);
      part.mesh.position.copy(part.at).sub(origin);
      part.mesh.rotation.x += seconds * 0.6;
      part.mesh.rotation.y += seconds * 0.4;

      if (collecting && part.at.distanceTo(shipAt) <= COLLECT_RANGE) {
        collected.push(part.type);
        group.remove(part.mesh);
        (part.mesh as THREE.Mesh).material instanceof THREE.Material &&
          ((part.mesh as THREE.Mesh).material as THREE.Material).dispose();
        debris.splice(i, 1);
      }
    }

    return collected;
  };

  return {
    group,
    spark,
    drop,
    step,
    dispose: () => {
      for (const burst of bursts) {
        burst.points.geometry.dispose();
        (burst.points.material as THREE.Material).dispose();
      }
      for (const part of debris) {
        const material = (part.mesh as THREE.Mesh).material;
        if (material instanceof THREE.Material) material.dispose();
      }
      partGeometry.dispose();
      sparkGeometry.dispose();
    },
  };
}
